import { QueryClient } from '@tanstack/react-query';

// ────────────────────────────────────────────────────────────
// React Query client — Phase 1 server-state caching template
// ────────────────────────────────────────────────────────────
//
// This is the single, app-wide QueryClient. It is created once here and
// provided at the app root (see `src/main.tsx`). Every server-state read
// hook in the app shares this cache, so navigating between screens reuses
// already-fetched data instead of refetching on every mount.
//
// Defaults are deliberately conservative for an admin CRUD app:
//   • staleTime 30s   — a fetched query is considered "fresh" for 30s.
//                       Within that window, re-mounting a hook (e.g.
//                       navigating away and back) serves the cached data
//                       WITHOUT a network request. This is the core win.
//   • gcTime 5m       — how long unused/inactive query data stays in the
//                       cache before garbage collection. Generous so brief
//                       navigations keep their data warm.
//   • retry 1         — retry a failed query once, then surface the error.
//                       Admin actions should fail fast and visibly, not
//                       hammer the API.
//   • refetchOnWindowFocus false
//                     — do NOT refetch every time the tab regains focus.
//                       For this app that would be surprising churn; we
//                       invalidate explicitly after mutations instead.
//
// When you need fresher data than staleTime allows for a specific read,
// override `staleTime` on that individual `useQuery` call — don't lower
// the global default.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 seconds
      gcTime: 5 * 60_000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
